import React, { useState, useEffect } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { signInAnonymously, onAuthStateChanged, getRedirectResult, signOut } from "firebase/auth";
import type { Period } from './types';
import { TOK, D1, FONT, d1 } from './design/tokens';
import { GeoLogo } from './design/GeoLogo';
import { db, auth, PERIODS_COL } from './firebase/config';

// Whitelist da conta docente (mesma do TeacherLogin) — valida o retorno do redirect Google no mobile
const TEACHER_EMAIL = 'profalauravalencia@gmail.com';

// ── Google Fonts import ───────────────────────────────────────────────────────
const _gfFontStyle = document.createElement('style');
_gfFontStyle.textContent = "@import url('https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,400;8..60,600;8..60,700&family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600;700&display=swap');";
if (!document.getElementById('gf-font-import')) { _gfFontStyle.id = 'gf-font-import'; document.head.appendChild(_gfFontStyle); }

import { TeacherDashboard } from './components/TeacherDashboard';
import { StudentPanel } from './components/StudentPanel';
import { TeacherLogin } from './components/TeacherLogin';
import { PrivacyPolicy } from './components/PrivacyPolicy';

export default function App() {
  const _initView = (():("home"|"teacher"|"student") => { if(localStorage.getItem("gf_teacher_ok")==="1") return "teacher"; try{ const s=localStorage.getItem("gf_student_session"); if(s&&JSON.parse(s)?.id) return "student"; }catch(e2){} return "home"; })();
  const [view,setView]=useState<"home"|"teacher"|"student">(_initView);
  const [teacherOk,setTeacherOk]=useState<boolean>(false); // P5: set after auth validation
  const [ready,setReady]=useState(false);
  const [activePeriod,setActivePeriod]=useState<Period|null>(()=>{ try{return JSON.parse(localStorage.getItem('gf_active_period')||'null');}catch{return null;} });
  const [showPrivacy,setShowPrivacy]=useState(false);

  useEffect(()=>{ const p=new URLSearchParams(window.location.search); if(p.get("token")) setView("student"); },[]);
  // ── Retorno do login Google via redirect (mobile) ────────────────────────────
  // No celular o login usa signInWithRedirect: a pagina recarrega ao voltar do Google.
  // Este handler roda no App (sempre montado) e completa o login mesmo na tela home.
  useEffect(()=>{
    getRedirectResult(auth).then((result)=>{
      if(result && result.user){
        if(result.user.email === TEACHER_EMAIL){
          try{ localStorage.setItem("gf_teacher_ok","1"); }catch(e){}
          setTeacherOk(true);
          setView("teacher");
          setReady(true);
        } else {
          signOut(auth).catch(()=>{});
        }
      }
    }).catch(e=>{ console.warn('[GeoFreq] getRedirectResult (App):', e); });
  },[]);
  useEffect(()=>{
    // Safety net: garante que a tela carrega mesmo se App Check/sign-in falhar
    // (ex.: Safari iOS bloqueando reCAPTCHA v3 por ITP)
    const failsafe = setTimeout(()=>setReady(true), 4000);
    const unsub=onAuthStateChanged(auth,u=>{
      if(u){
        clearTimeout(failsafe);
        setReady(true);
        // P5: only trust gf_teacher_ok if Firebase user is real (not anonymous)
        if(!u.isAnonymous && localStorage.getItem("gf_teacher_ok")==="1"){
          setTeacherOk(true);
        } else if(u.isAnonymous && localStorage.getItem("gf_teacher_ok")==="1"){
          localStorage.removeItem("gf_teacher_ok"); // stale flag
        }
      } else {
        signInAnonymously(auth).catch(e=>{ console.warn('[GeoFreq] anon sign-in falhou:',e); setReady(true); });
      }
    });
    return ()=>{ clearTimeout(failsafe); unsub(); };
  },[]);
  const [_appPeriodFailed, _setAppPeriodFailed] = React.useState(false);
  useEffect(()=>{
    try { const cp=localStorage.getItem('gf_active_period'); if(cp) setActivePeriod(JSON.parse(cp)); } catch {}
    if (!navigator.onLine) { window.addEventListener('online', () => setActivePeriod(null), {once:true}); return; }
    const unsub=onSnapshot(collection(db,PERIODS_COL),
      snap=>{ _setAppPeriodFailed(false); const ps=snap.docs.map(d=>({...d.data(), id:d.id} as Period)); const active=ps.find(p=>p.active)||null; setActivePeriod(active); try { if(active) localStorage.setItem('gf_active_period', JSON.stringify(active)); } catch {} },
      _err=>{ console.warn('[GeoFreq] Falha ao carregar periodos (App):', _err); _setAppPeriodFailed(true); }
    );
    return unsub;
  },[]);

  if(!ready) return <div style={{minHeight:"100vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",fontFamily:TOK.sans,background:TOK.bg,gap:14}}><GeoLogo size={60}/><div style={{color:TOK.ink30,fontSize:13,fontFamily:TOK.sans}}>Carregando…</div></div>;
  if(view==="teacher"&&!teacherOk) return <TeacherLogin onLogin={()=>{setTeacherOk(true);localStorage.setItem("gf_teacher_ok","1");}} onBack={()=>setView("student")}/>;
  if(view==="teacher"&&teacherOk) return <TeacherDashboard onLogout={()=>{setTeacherOk(false);localStorage.removeItem("gf_teacher_ok");setView("home");}}/>;
  if(view==="student") return <StudentPanel activePeriod={activePeriod} onBack={()=>{localStorage.removeItem("gf_student_session");setView("home");window.history.replaceState({},"",window.location.pathname);}}/>;

  return (
    <>
    {showPrivacy && <PrivacyPolicy onClose={()=>setShowPrivacy(false)}/>}
    <div style={{minHeight:"100vh",background:D1.paper,fontFamily:FONT.sans,display:"flex",flexDirection:"column"}}>
      <div style={{background:D1.acc,color:"#fff",fontSize:11,letterSpacing:0.6,padding:"6px 32px"}}>GEO Frequência</div>
      <div style={{flex:1,display:"grid",placeItems:"center",padding:"24px 16px"}}>
        <div style={{width:"100%",maxWidth:400,background:"#fff",border:`1px solid ${D1.line}`,padding:"36px 40px",boxSizing:"border-box"}}>
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:26,paddingBottom:20,borderBottom:`1px solid ${D1.line2}`}}>
            <GeoLogo size={44}/>
            <div>
              <div style={{fontFamily:FONT.serif,fontSize:22,fontWeight:600,lineHeight:1.2}}>GEO Frequência</div>
              <div style={{...d1.cap,fontSize:10}}>{activePeriod?`Período ativo ${activePeriod.label}`:"Registro de frequência discente"}</div>
            </div>
          </div>
          <h1 style={{...d1.h1,fontSize:26,marginBottom:6}}>Bem-vindo(a)</h1>
          <div style={{fontSize:13,color:D1.ink2,marginBottom:22}}>Escolha como deseja entrar.</div>
          <button style={{...d1.btn,width:"100%",padding:13,marginBottom:10}} onClick={()=>setView("student")}>Sou aluno(a)</button>
          <button style={{...d1.btnO,width:"100%",padding:13}} onClick={()=>setView("teacher")}>Sou professora</button>
          <div style={{marginTop:18,textAlign:"center" as const}}>
            <button onClick={()=>setShowPrivacy(true)} style={{background:"none",border:"none",cursor:"pointer",color:D1.ink3,fontSize:12.5,textDecoration:"underline",fontFamily:FONT.sans,padding:0}}>Política de Privacidade (LGPD)</button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
